import {
   Flex,
   Link,
   Button,
   Menu,
   MenuButton,
   MenuList,
   MenuItem,
   LinkProps,
   MenuItemProps,
   ButtonProps,
   IconButton,
   useBreakpoint,
   useDisclosure,
   Tooltip,
} from '@chakra-ui/react';
import { FaIcon } from '@ifixit/icons';
import {
   faPenToSquare,
   faArrowsMaximize,
   faClockRotateLeft,
   faCircleStar,
   faDownload,
   faLanguage,
   faLinkSimple,
   faCode,
   faBellSlash,
   IconDefinition,
   faAngleDown,
   faMessagePlus,
   faBell,
   faMessageMinus,
   faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircleStar as faFilledCircleStar } from '@fortawesome/pro-solid-svg-icons';
import { _js } from '@ifixit/localize';
import { ReactNode, useContext, MouseEventHandler } from 'react';
import { GuideSubscriptionContext, withGuideSubscription } from './GuideSubscription';
import { GuideFavoriteContext, withGuideFavorite } from './GuideFavorite';
import { GuideEmbedModal } from './GuideEmbedModal';
import { useExpandCollapseComments } from './GuideComments';
import { BreadCrumbs } from 'breadcrumbs/Breadcrumbs';
import { initializeChakraComponent } from 'Shared/chakra-initialize';
import { FlexScrollGradient } from './FlexScrollGradient';
import copyToClipboard from 'Shared/copy_to_clipboard';

export interface NavData {
   breadCrumbs: Array<BreadcrumbData>;
   answersPartsUrl: string;
   devicePartsUrl?: string | null;
   fullScreenUrl: string;
   sharableUrl: string;
   historyUrl?: string;
   pdfUrl?: string;
   translateUrl?: string;
   editUrl?: string;
   editTranslateUrl?: string;
   isTeardown: boolean;
   showExpandComments?: true;
}

interface BreadcrumbData {
   name: string;
   url: string;
}

export function GuideNav(props: NavData) {
   const { breadCrumbs, answersPartsUrl, devicePartsUrl } = props;
   const devicePageUrl = breadCrumbs[breadCrumbs.length - 1].url;

   const breakpoint = useBreakpoint({ ssr: false });
   const isMobile = breakpoint == 'base';

   return (
      <Flex flexDirection="column" width="100%">
         <Flex justifyContent="center" borderBottom="1px solid" borderColor="gray.200">
            <Flex
               flexGrow={1}
               height="48px"
               width="100%"
               maxWidth="1200px"
               paddingLeft={{ base: '0px', sm: '20px', md: '20px' }}
               paddingRight="0px"
               justifyContent="space-between"
            >
               {!isMobile && (
                  <Flex flexGrow={1} flexShrink={0} flexBasis={0}>
                     <BreadCrumbs breadCrumbs={breadCrumbs} />
                  </Flex>
               )}
               <NavTabs
                  answersPartsUrl={answersPartsUrl}
                  devicePartsUrl={devicePartsUrl}
                  devicePageUrl={devicePageUrl}
               />
               <EditMenu {...props} />
            </Flex>
         </Flex>
         {isMobile && <BreadCrumbs breadCrumbs={breadCrumbs} paddingLeft="12px" />}
      </Flex>
   );
}

function NavTabs({
   answersPartsUrl,
   devicePartsUrl,
   devicePageUrl,
}: Pick<NavData, 'answersPartsUrl' | 'devicePartsUrl'> & { devicePageUrl: string }) {
   const showParts = devicePartsUrl !== undefined;

   const baseStyleProps: LinkProps = {
      outline: '2px solid transparent',
      outlineOffset: '2px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 'md',
      paddingTop: 2,
      paddingBottom: 2,
      paddingInlineStart: 4,
      paddingInlineEnd: 4,
      position: 'relative',
   };

   const bottomFeedbackStyleProps = {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      borderRadius: '2px 2px 0px 0px',
   };

   const selectedStyleProps = {
      ...baseStyleProps,
      borderColor: 'brand.500',
      color: 'gray.900',
      fontWeight: 500,
      sx: {
         '&:visited': {
            color: 'gray.900',
         },
         '&:hover': {
            textDecoration: 'none',
            background: 'gray.100',
            '::after': {
               background: 'brand.700',
            },
         },
         '::after': {
            ...bottomFeedbackStyleProps,
            background: 'brand.500',
         },
      },
   };

   const notSelectedStyleProps = {
      ...baseStyleProps,
      borderColor: 'transparent',
      color: 'gray.500',
      fontWeight: 400,
      sx: {
         '&:visited': {
            color: 'gray.500',
         },
         '&:hover': {
            textDecoration: 'none',
         },
         '&:hover:not(.isDisabled)': {
            textDecoration: 'none',
            color: 'gray.700',
            background: 'gray.100',
         },
         '&.isDisabled': {
            opacity: 0.4,
            cursor: 'not-allowed',
            color: 'gray.700',
            background: 'gray.100',
         },
      },
   };

   return (
      <>
         <FlexScrollGradient height="100%" overflow="hidden" flexShrink={1}>
            {showParts && (
               <Link
                  {...notSelectedStyleProps}
                  href={devicePartsUrl}
                  className={devicePartsUrl ? undefined : 'isDisabled'}
               >
                  {_js('Parts')}
               </Link>
            )}
            <Link {...selectedStyleProps} href={devicePageUrl}>
               {_js('Guides')}
            </Link>
            <Link {...notSelectedStyleProps} href={answersPartsUrl}>
               {_js('Answers')}
            </Link>
         </FlexScrollGradient>
      </>
   );
}

type StyledMenuItemProps = {
   as?: typeof Link | 'button';
   href?: string;
   icon?: IconDefinition;
   children?: ReactNode;
} & Omit<LinkProps | ButtonProps, 'link'> &
   Omit<MenuItemProps, 'icon'>;

function StyledMenuItem({ as, icon, children, ...menuItemProps }: StyledMenuItemProps) {
   // If link but no href, bail
   if (as == Link && !menuItemProps.href) {
      return null;
   }

   return (
      <MenuItem
         icon={
            icon ? (
               <FaIcon icon={icon} fontSize="16px" maxWidth="16px" width="16px" color="gray.600" />
            ) : undefined
         }
         as={as || 'button'}
         color="gray.900"
         _visited={{ color: menuItemProps.color || 'gray.900' }}
         _hover={{ textDecoration: 'none' }}
         fontSize="14px"
         {...menuItemProps}
      >
         {children}
      </MenuItem>
   );
}

function EditMenu({
   fullScreenUrl,
   sharableUrl,
   historyUrl,
   pdfUrl,
   translateUrl,
   editUrl,
   editTranslateUrl,
   showExpandComments,
   isTeardown,
}: NavData & { isTeardown: boolean }) {
   const guideSubscriptionContext = useContext(GuideSubscriptionContext);
   const guideFavoriteContext = useContext(GuideFavoriteContext);
   const { isOpen: isEmbedOpen, onOpen: onEmbedOpen, onClose: onEmbedClose } = useDisclosure();
   const [isExpanded, toggleExpandComments] = useExpandCollapseComments();

   return (
      <Flex>
         {(editUrl || editTranslateUrl) && (
            <Button
               as={Link}
               height="100%"
               color="brand.500"
               borderRadius="0px"
               borderColor="gray.200"
               border="none"
               borderLeft="1px solid"
               flexShrink={0}
               sx={{ '&': { borderColor: 'gray.200' } }}
               leftIcon={<FaIcon icon={faPenToSquare} fontSize="16px" color="brand.500" />}
               href={editUrl || editTranslateUrl}
               _visited={{ color: 'brand.500' }}
               _hover={{ textDecoration: 'none', background: 'gray.100' }}
               id="edit-nav-link"
            >
               {editUrl ? _js('Edit') : _js('Translate')}
            </Button>
         )}
         <Menu closeOnSelect={true} isLazy={true} lazyBehavior="keepMounted">
            <MenuButton
               as={IconButton}
               height="100%"
               color="brand.500"
               borderRadius="0px"
               border="none"
               borderColor="gray.200"
               borderLeft="1px solid"
               borderRight="1px solid"
               sx={{ '&': { borderColor: 'gray.200' } }}
               flexShrink={0}
               padding="0px 16px"
               icon={<FaIcon icon={faAngleDown} fontSize="16px" color="brand.500" />}
               className="guide-options-dropdown"
               aria-label={_js('Guide Options Menu')}
            />
            <MenuList zIndex={3} className="guide-options-dropdown-list">
               <StyledMenuItem
                  icon={faArrowsMaximize}
                  as={Link}
                  href={fullScreenUrl}
                  className="fullscreen-nav-link"
               >
                  {_js('Full screen')}
               </StyledMenuItem>
               <StyledMenuItem
                  icon={faClockRotateLeft}
                  as={Link}
                  href={historyUrl}
                  className="link-history"
               >
                  {_js('History')}
               </StyledMenuItem>
               {isTeardown && (
                  <StyledMenuItem
                     icon={guideFavoriteContext.isFavorite ? faFilledCircleStar : faCircleStar}
                     onClick={() => {
                        guideFavoriteContext.isFavorite
                           ? guideFavoriteContext.unfavorite(App.guideid, App.langid, isTeardown)
                           : guideFavoriteContext.favorite(App.guideid, App.langid, isTeardown);
                     }}
                     closeOnSelect={false}
                  >
                     {guideFavoriteContext.isFavorite
                        ? _js('Remove from Favorites')
                        : _js('Save to Favorites')}
                  </StyledMenuItem>
               )}
               <StyledMenuItem icon={faDownload} as={Link} href={pdfUrl}>
                  {_js('Download PDF')}
               </StyledMenuItem>
               <StyledMenuItem icon={faLanguage} as={Link} href={translateUrl}>
                  {_js('Translate')}
               </StyledMenuItem>
               {showExpandComments && (
                  <StyledMenuItem
                     icon={isExpanded ? faMessageMinus : faMessagePlus}
                     onClick={toggleExpandComments}
                     className="expand-comments"
                     closeOnSelect={false}
                  >
                     {isExpanded ? _js('Collapse Comments') : _js('Expand Comments')}
                  </StyledMenuItem>
               )}
               <CopyableStyledMenuItem
                  icon={faLinkSimple}
                  className="show-shareable-link"
                  closeOnSelect={false}
                  copyText={sharableUrl}
               >
                  {_js('Get shareable link')}
               </CopyableStyledMenuItem>
               <StyledMenuItem icon={faCode} onClick={onEmbedOpen} className="show-embed">
                  {_js('Embed this guide')}
                  <GuideEmbedModal
                     isOpen={isEmbedOpen}
                     onClose={onEmbedClose}
                     guideid={App.guideid}
                  />
               </StyledMenuItem>
               <StyledMenuItem
                  icon={guideSubscriptionContext.isSubscribed ? faBellSlash : faBell}
                  onClick={() => {
                     guideSubscriptionContext.isSubscribed
                        ? guideSubscriptionContext.unsubscribe(App.guideid, App.langid)
                        : guideSubscriptionContext.subscribe(App.guideid, App.langid);
                  }}
                  closeOnSelect={false}
               >
                  {guideSubscriptionContext.isSubscribed
                     ? _js('Stop Notifications')
                     : _js('Notify me on changes')}
               </StyledMenuItem>
            </MenuList>
         </Menu>
      </Flex>
   );
}

function CopyableStyledMenuItem({
   children,
   hideAfterMs = 1000,
   copyText,
   ...props
}: {
   hideAfterMs?: number;
   copyText: string;
   children: ReactNode;
} & StyledMenuItemProps) {
   const disclosureProps = useDisclosure(props);

   const onClick: MouseEventHandler<HTMLButtonElement> = () => {
      disclosureProps.onOpen();
      copyToClipboard(copyText);
      setTimeout(disclosureProps.onClose, hideAfterMs);
   };

   const icon = disclosureProps.isOpen ? faCheck : props.icon;

   return (
      <StyledMenuItem {...props} icon={icon} onClick={onClick}>
         <Tooltip hasArrow {...disclosureProps} label={_js('Copied!')}>
            {children}
         </Tooltip>
      </StyledMenuItem>
   );
}

initializeChakraComponent('GuideNavComponent', withGuideFavorite(withGuideSubscription(GuideNav)));
